import type { MarketSignal } from '@/features/home/lib/home-opportunities'

/**
 * The brief's topic gauges (Alex: "an immediate overview of a topic… a score for each"): one
 * 0–100 reading each for derivatives positioning, money flow, market sentiment and news heat,
 * on the coins the user cares about (BTC and ETH when there are none). Bearish → bullish for
 * the first three; quiet → hot for news, which has no direction. Each lists the inputs that
 * moved it, so the number can be explained. Pure (tested in __tests__/home-gauges.test.ts).
 */

export type GaugeTopic = 'derivatives' | 'flow' | 'sentiment' | 'news'
export type GaugeReading = 'bearish' | 'leanBearish' | 'neutral' | 'leanBullish' | 'bullish'
export type HeatReading = 'quiet' | 'normal' | 'busy' | 'hot'

export type GaugeInputKey =
  | 'funding'
  | 'oiTrend'
  | 'etfFlows'
  | 'takers'
  | 'breadth'
  | 'majors'
  | 'liquidations'
  | 'headlines'

export interface GaugeInput {
  key: GaugeInputKey
  /** −1 (bearish) … +1 (bullish); for news, 0 … 1 heat. */
  contribution: number
  params: Record<string, string | number>
}

export interface Gauge {
  topic: GaugeTopic
  /** 0–100, or null when nothing fed it. */
  score: number | null
  reading: GaugeReading | HeatReading | null
  inputs: GaugeInput[]
}

export interface GaugesInput {
  /** Every screener coin (breadth reads the whole market). */
  signals: readonly MarketSignal[]
  /** The coins the gauges focus on. */
  focus: readonly string[]
  /** Daily net ETF flows (USD), oldest first, per coin. */
  etfFlows?: ReadonlyMap<string, readonly number[]>
  /** Headlines naming one of the focus coins in the last 24 hours. */
  headlines24h?: number
}

const ETF_FLOOR: Record<string, number> = { BTC: 300e6, ETH: 75e6, SOL: 25e6 }
/** A normal day's headline count on a handful of coins; twice this is "hot". */
const NORMAL_HEADLINES = 12

function squash(value: number): number {
  return Math.tanh(value)
}

function mean(values: number[]): number | null {
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null
}

function toScore(contributions: number[]): number | null {
  const average = mean(contributions)
  return average === null ? null : Math.round(50 + 50 * Math.max(-1, Math.min(1, average)))
}

export function readingOf(score: number): GaugeReading {
  if (score < 25) return 'bearish'
  if (score < 42) return 'leanBearish'
  if (score <= 58) return 'neutral'
  if (score <= 75) return 'leanBullish'
  return 'bullish'
}

export function heatOf(score: number): HeatReading {
  if (score < 25) return 'quiet'
  if (score < 55) return 'normal'
  if (score < 80) return 'busy'
  return 'hot'
}

function round(value: number, digits = 1): number {
  return Number(value.toFixed(digits))
}

export function buildGauges(input: GaugesInput): Gauge[] {
  const focus = new Set(input.focus.length ? input.focus : ['BTC', 'ETH'])
  const focused = input.signals.filter((signal) => focus.has(signal.asset))

  // Derivatives: what positioning pays for, and whether new money opens with or against price.
  const derivatives: GaugeInput[] = []
  const funding = mean(focused.map((s) => s.fundingApr).filter((v): v is number => v !== null))
  if (funding !== null) {
    derivatives.push({
      key: 'funding',
      contribution: squash(funding / 30),
      params: { apr: round(funding) },
    })
  }
  const oiTrend = mean(
    focused
      .filter((s) => s.oiChange24h !== null && s.change24h !== null)
      .map((s) => Math.sign(s.change24h!) * squash(s.oiChange24h! / 10))
  )
  const oiChange = mean(focused.map((s) => s.oiChange24h).filter((v): v is number => v !== null))
  if (oiTrend !== null && oiChange !== null) {
    derivatives.push({
      key: 'oiTrend',
      contribution: oiTrend,
      params: { oi: round(oiChange), direction: oiTrend >= 0 ? 'with' : 'against' },
    })
  }

  // Flow: institutions (ETF) and takers.
  const flow: GaugeInput[] = []
  for (const asset of focus) {
    const flows = input.etfFlows?.get(asset)
    if (!flows || flows.length === 0) continue
    const last3 = flows.slice(-3).reduce((sum, value) => sum + value, 0)
    flow.push({
      key: 'etfFlows',
      contribution: squash(last3 / (ETF_FLOOR[asset] ?? 50e6)),
      params: { asset, total: Math.round(last3) },
    })
  }
  const takers = mean(focused.map((s) => s.takerBuyPct15m).filter((v): v is number => v !== null))
  if (takers !== null) {
    flow.push({
      key: 'takers',
      contribution: Math.max(-1, Math.min(1, (takers - 50) / 15)),
      params: { pct: Math.round(takers) },
    })
  }

  // Sentiment: how the whole market feels — breadth, the majors, who's getting liquidated.
  const sentiment: GaugeInput[] = []
  const withChange = input.signals.filter((s) => s.change24h !== null)
  if (withChange.length >= 10) {
    const up = withChange.filter((s) => (s.change24h ?? 0) > 0).length / withChange.length
    sentiment.push({
      key: 'breadth',
      contribution: (up - 0.5) * 2,
      params: { pct: Math.round(up * 100), count: withChange.length },
    })
  }
  const majors = input.signals.filter(
    (s) => (s.asset === 'BTC' || s.asset === 'ETH') && s.change24h !== null
  )
  const majorsMove = mean(majors.map((s) => s.change24h!))
  if (majorsMove !== null) {
    sentiment.push({
      key: 'majors',
      contribution: squash(majorsMove / 5),
      params: { change: round(majorsMove) },
    })
  }
  const longs = input.signals.reduce((sum, s) => sum + (s.liqLongUsd15m ?? 0), 0)
  const shorts = input.signals.reduce((sum, s) => sum + (s.liqShortUsd15m ?? 0), 0)
  if (longs + shorts > 0) {
    sentiment.push({
      key: 'liquidations',
      contribution: (shorts - longs) / (shorts + longs),
      params: { longs: Math.round(longs), shorts: Math.round(shorts) },
    })
  }

  // News: how much is being said about these coins today (no direction).
  const news: GaugeInput[] = []
  if (input.headlines24h !== undefined) {
    const heat = Math.min(1, input.headlines24h / (NORMAL_HEADLINES * 2))
    news.push({ key: 'headlines', contribution: heat, params: { count: input.headlines24h } })
  }

  const directional = (topic: GaugeTopic, inputs: GaugeInput[]): Gauge => {
    const score = toScore(inputs.map((i) => i.contribution))
    return { topic, score, reading: score === null ? null : readingOf(score), inputs }
  }
  const newsScore = news.length ? Math.round(news[0].contribution * 100) : null
  return [
    directional('derivatives', derivatives),
    directional('flow', flow),
    directional('sentiment', sentiment),
    {
      topic: 'news',
      score: newsScore,
      reading: newsScore === null ? null : heatOf(newsScore),
      inputs: news,
    },
  ]
}
